import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { format } from "date-fns";
import prettyBytes from "pretty-bytes";
import { useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { Icon } from "@/components/icon";
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { CardTitle } from "@/components/ui/card";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { CardUpdateDialog } from "@/dialogs/card-update-dialog";
import { cn } from "@/utils";
import type { meuPlanoCancelSubscriptionMutation } from "./__generated__/meuPlanoCancelSubscriptionMutation.graphql";
import type { meuPlanoQuery } from "./__generated__/meuPlanoQuery.graphql";

function formatUsagePair(
	usage: number,
	limit: number,
	isBytes: boolean,
): { usageDisplay: string; limitDisplay: string } {
	if (!isBytes) {
		return { usageDisplay: String(usage), limitDisplay: String(limit) };
	}

	const limitFormatted = prettyBytes(limit);
	const spaceIdx = limitFormatted.indexOf(" ");
	if (spaceIdx === -1) {
		return { usageDisplay: String(usage), limitDisplay: limitFormatted };
	}

	const limitNum = parseFloat(limitFormatted.substring(0, spaceIdx));
	if (limitNum === 0) {
		return { usageDisplay: "0", limitDisplay: limitFormatted };
	}

	const factor = limit / limitNum;
	const usageInSameUnit = usage / factor;
	const usageRounded = Math.round(usageInSameUnit * 10) / 10;
	const usageStr = Number.isInteger(usageRounded)
		? usageRounded.toString()
		: usageRounded.toFixed(1);

	return { usageDisplay: usageStr, limitDisplay: limitFormatted };
}

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/configuracoes/meu-plano",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const [cardDialogOpen, setCardDialogOpen] = useState(false);
	const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
	const { club } = useRouteContext({ from: Route.id });

	const [cancelSubscription, isCancelling] =
		useMutation<meuPlanoCancelSubscriptionMutation>(graphql`
			mutation meuPlanoCancelSubscriptionMutation($id: ID!) {
				cancelClubSubscription(id: $id) {
					id
					canceledAt
				}
			}
		`);

	const data = useLazyLoadQuery<meuPlanoQuery>(
		graphql`
    query meuPlanoQuery($slug: String!) {
      club(slug: $slug) {
        id
        subscription {
          id
          startAt
          nextBillingAt
          discounts {
	          value
          }
          features {
            key
            value
          }
          currentCycle {
            startAt
            endAt
          }
          addons {
            key
            value
            count
          }
          nextBillingAt
          plan {
            name
            rawPrice
          }
          invoices {
            dueAt
            status
            charge {
              amount
              paidAt
              status
            }
            createdAt
          }
          paymentMethod {
            method
            card {
              brand
              lastFourDigits
              expMonth
              expYear
            }
          }
          usage {
            key
            label
            usage
            value
            isLimited
            overusageCharge
            overusagePackageQnt
            overusagePackagePrice
          }
        }
      }
    }
  `,
		{ slug: club?.slug },
	);

	const sortedInvoices = [...(data.club.subscription?.invoices || [])].sort(
		(a, b) => new Date(b.dueAt).getTime() - new Date(a.dueAt).getTime(),
	);
	const isPaymentPending = sortedInvoices[0]?.charge.status === "PENDING";
	const lastPaidInvoice = sortedInvoices
		.filter(
			(invoice) => invoice.charge.status === "PAID" && invoice.charge.paidAt,
		)
		.sort(
			(a, b) =>
				new Date(b.charge.paidAt).getTime() -
				new Date(a.charge.paidAt).getTime(),
		)[0];

	const addonsUsage =
		data.club.subscription?.addons?.reduce(
			(acc, addon) => acc + addon.value,
			0,
		) || 0;
	const discounts =
		data.club.subscription?.discounts?.reduce(
			(acc, discount) => acc + discount.value / 100,
			0,
		) || 0;

	const currentPrice =
		(data.club.subscription?.plan.rawPrice || 0) / 100 +
		addonsUsage -
		discounts;

	const addons =
		data.club.subscription?.addons?.reduce<
			Record<
				string,
				{ readonly count: number; readonly key: string; readonly value: number }
			>
		>((acc, ad) => {
			acc[ad.key] = ad;

			return acc;
		}, {}) || {};

	return (
		<div className="p-6 flex flex-col gap-6">
			<div className="flex flex-col gap-6 border rounded-md p-6">
				<CardTitle>Histórico de Cobrança</CardTitle>

				<div className="flex flex-row gap-3">
					<div className="flex-1 flex flex-col gap-6">
						<div className="flex flex-col flex-1 gap-1.5 h-22 max-h-22 min-h-22">
							<span className="text-sm">Último Pagamento</span>

							<span className="text-muted-foreground text-sm">
								{isPaymentPending
									? "Pagamento Pendente"
									: lastPaidInvoice
										? format(
												new Date(lastPaidInvoice.charge.paidAt),
												"dd/MM/yyyy",
											)
										: "-"}
							</span>

							<span className="text-3xl text-muted-foreground">
								{!isPaymentPending && lastPaidInvoice
									? (lastPaidInvoice.charge.amount / 100).toLocaleString(
											"pt-BR",
											{
												style: "currency",
												currency: "BRL",
											},
										)
									: "-"}
							</span>
						</div>

						<Separator />

						<div className="w-full flex-1 shrink-0">
							<div className="font-medium mb-3">Últimas Faturas</div>

							<div className="gap-1.5 flex flex-col w-full">
								{sortedInvoices?.length ? (
									sortedInvoices
										.slice(1, sortedInvoices.length - 1)
										.map((inv, _index) => (
											<div
												key={"invoice-${index}"}
												className="text-muted-foreground gap-18 flex flex-row items-center"
											>
												<span
													className={cn(
														inv.charge.status === "CANCELED"
															? "line-through"
															: "",
													)}
												>
													{format(new Date(inv.charge.paidAt), "dd/MM/yyyy")}
												</span>
												<span
													className={cn(
														inv.charge.status === "CANCELED"
															? "line-through"
															: "",
													)}
												>
													{(inv.charge.amount / 100).toLocaleString("pt-BR", {
														style: "currency",
														currency: "BRL",
													})}
												</span>

												<span className="flex flex-row gap-1.5 items-center text-emerald-600">
													{inv.charge.status === "CANCELED" && (
														<span className="text-muted-foreground">
															Cancelado
														</span>
													)}
													{inv.charge.status === "FAILED" && (
														<span className="text-muted-foreground">
															Falhou
														</span>
													)}
													{inv.charge.status === "PAID" && (
														<>
															<Icon name="Check" className="size-4" />
															Pago
														</>
													)}
													{inv.charge.status === "PENDING" && (
														<span className="text-destructive">Pendente</span>
													)}
												</span>
											</div>
										))
								) : (
									<div>
										<span className="text-muted-foreground">
											Nenhuma fatura encontrada
										</span>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="flex-1 flex flex-col gap-6 items-start justify-between">
						<div className="flex flex-col flex-1 gap-1.5 h-22 max-h-22 min-h-22">
							<span className="text-sm">Próximo Pagamento</span>

							<span className="text-muted-foreground text-sm">
								{data.club.subscription?.nextBillingAt
									? format(
											new Date(data.club.subscription?.nextBillingAt),
											"dd/MM/yyyy",
										)
									: "-"}
							</span>

							<span className="text-3xl">
								{currentPrice.toLocaleString("pt-BR", {
									style: "currency",
									currency: "BRL",
								})}
							</span>
						</div>

						<Separator />

						<div className="w-full flex-1 shrink-0">
							<div className="font-medium mb-3">Método de Pagamento</div>

							<div className="gap-1.5 flex flex-col w-full">
								{data.club?.subscription?.paymentMethod?.method === "BOLETO" ? (
									<div className="text-muted-foreground gap-18 flex flex-row items-center">
										<div className="flex flex-row items-center justify-start gap-3">
											<span className="text-foreground">Boleto</span>
										</div>
									</div>
								) : null}

								{data.club?.subscription?.paymentMethod?.method ===
								"CREDIT_CARD" ? (
									<div className="text-muted-foreground gap-18 flex flex-row items-center">
										<div className="flex flex-row items-center justify-start gap-3">
											<span className="text-foreground">
												<Icon name="CreditCard" />
											</span>
											<span>
												•••• •••• ••••{" "}
												{
													data.club?.subscription?.paymentMethod.card
														?.lastFourDigits
												}
											</span>
										</div>

										<Dialog
											open={cardDialogOpen}
											onOpenChange={setCardDialogOpen}
										>
											<DialogTrigger asChild>
												<Button size="sm" variant="ghost">
													Alterar
												</Button>
											</DialogTrigger>

											{cardDialogOpen && (
												<CardUpdateDialog
													subscriptionId={data.club.subscription?.id}
													onRequestClose={() => setCardDialogOpen(false)}
												/>
											)}
										</Dialog>
									</div>
								) : null}

								<div>
									<span>&nbsp;</span>
								</div>
								<div>
									<span>&nbsp;</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="rounded-md border p-6 flex flex-col gap-6">
				<div className="flex flex-row items-center justify-between">
					<CardTitle>Detalhamento de Uso</CardTitle>

					{/*<div className="flex flex-row items-center justify-start gap-3 text-muted-foreground">
						<Button
							variant="ghost"
							size="icon-sm"
							className="text-muted-foreground"
						>
							<Icon name="ChevronLeft" />
						</Button>

						<span className="text-sm text-foreground">Período Atual</span>

						<Button
							variant="ghost"
							size="icon-sm"
							className="text-muted-foreground"
						>
							<Icon name="ChevronRight" />
						</Button>
					</div>*/}
				</div>

				<div className="grid grid-cols-3 gap-3 text-muted-foreground">
					{data.club.subscription?.usage
						.filter((u) => u.isLimited)
						.map((it) => {
							const isBytes = it.key === "storage" || it.key === "bandwidth";
							const { usageDisplay, limitDisplay } = formatUsagePair(
								it.usage,
								it.value,
								isBytes,
							);
							return (
								<>
									<div>{it.label}</div>
									<div className="text-right">
										{usageDisplay} / {limitDisplay}
									</div>
									<div
										className={cn(
											"text-right",
											addons[it.key] ? "text-primary" : "",
										)}
									>
										{addons[it.key]
											? `+${addons[it.key].value} addons (${addons[it.key].value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })})`
											: "-"}
									</div>
								</>
							);
						})}
				</div>
			</div>

			<div className="rounded-md border p-6">
				<CardTitle>Gerenciar Assinatura</CardTitle>

				<Field
					orientation="horizontal"
					className="flex mt-6 flex-col lg:flex-row lg:gap-6 lg:items-center lg:justify-between"
				>
					<div className="flex flex-col gap-1.5 flex-1">
						<FieldLabel>Cancelar Plano</FieldLabel>

						<FieldDescription>
							Ao cancelar, você manterá acesso até o final do período pago (
							{data.club.subscription?.currentCycle?.endAt
								? format(
										new Date(data.club.subscription?.currentCycle?.endAt),
										"dd/MM/yyyy",
									)
								: "N/A"}
							). Seus dados serão preservados por 90 dias para eventual
							reativação.
						</FieldDescription>
					</div>

					<div>
						<AlertDialog
							open={cancelDialogOpen}
							onOpenChange={setCancelDialogOpen}
						>
							<Button
								variant="destructive"
								onClick={() => setCancelDialogOpen(true)}
							>
								Cancelar Plano
							</Button>

							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>Cancelar Plano</AlertDialogTitle>
								</AlertDialogHeader>

								<AlertDialogDescription asChild>
									<div className="flex flex-col gap-2 text-sm text-muted-foreground">
										<p>
											Tem certeza que deseja cancelar seu plano? Você manterá
											acesso até o final do período pago (
											{data.club.subscription?.currentCycle?.endAt
												? format(
														new Date(
															data.club.subscription?.currentCycle?.endAt,
														),
														"dd/MM/yyyy",
													)
												: "N/A"}
											).
										</p>
										<p>
											Após o cancelamento, seus dados serão preservados por 90
											dias caso deseje reativar sua assinatura.
										</p>
										<p>
											Se precisar que seus dados sejam mantidos por mais tempo,
											entre em contato conosco antes de cancelar.
										</p>
									</div>
								</AlertDialogDescription>

								<AlertDialogFooter>
									<Button
										variant="outline"
										onClick={() => setCancelDialogOpen(false)}
									>
										Voltar
									</Button>

									<Button
										variant="destructive"
										disabled={isCancelling}
										onClick={() => {
											if (!data.club.subscription?.id) return;
											cancelSubscription({
												variables: {
													id: data.club.subscription.id,
												},
												onCompleted: (_response, errors) => {
													if (errors?.length) {
														toast.error(errors[0].message);
														return;
													}
													toast.success("Assinatura cancelada com sucesso.");
													setCancelDialogOpen(false);
												},
												onError: () => {
													toast.error(
														"Não foi possível cancelar a assinatura. Tente novamente mais tarde.",
													);
												},
											});
										}}
									>
										Confirmar Cancelamento
										{isCancelling && <Spinner />}
									</Button>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					</div>
				</Field>
			</div>
		</div>
	);
}
